import { ShieldCheck, Zap, Layers, Scaling, Info } from 'lucide-react'
import { DocSection } from '@/components/docs/doc-section'
import { DocPageHeader } from '@/components/docs/doc-page-header'
import { DocNav } from '@/components/docs/doc-nav'
import { MermaidDiagram } from '@/components/docs/mermaid-diagram'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'

const accessControlModels = [
  {
    model: 'ACL',
    description: 'Per-object permission lists that map users directly to resources',
    granularity: 'Low',
    flexibility: 'Low',
  },
  {
    model: 'RBAC',
    description: 'Users are assigned roles, and roles carry permissions',
    granularity: 'Medium',
    flexibility: 'Medium',
  },
  {
    model: 'ABAC',
    description: 'Policies evaluate attributes of users, resources, actions, and context',
    granularity: 'High',
    flexibility: 'High',
  },
] as const

const granularityVariant = {
  Low: 'secondary',
  Medium: 'outline',
  High: 'default',
} as const

const abacBenefits = [
  {
    title: 'Fine-Grained Control',
    description:
      'Control access down to individual rows and columns. Mask the SSN column for analysts while keeping it visible to compliance officers — all with a single policy.',
    icon: Layers,
  },
  {
    title: 'Dynamic Decisions',
    description:
      'Decisions change with context. A query permitted during business hours from a corporate network can be denied at midnight from an unknown IP — no policy rewrite needed.',
    icon: Zap,
  },
  {
    title: 'Scalable',
    description:
      'Add new attributes — a department, a data classification, a project tag — without rewriting policies or reassigning roles. The system grows with your organization.',
    icon: Scaling,
  },
] as const

export function AbacPage() {
  return (
    <>
      <DocPageHeader icon={ShieldCheck} title="What is ABAC?" />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6 max-w-4xl">
        <DocSection title="Access Control Models">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Every system that protects data needs a model for deciding who gets access. The three
            most common models are <strong>ACLs</strong> (Access Control Lists),{' '}
            <strong>RBAC</strong> (Role-Based Access Control), and <strong>ABAC</strong>{' '}
            (Attribute-Based Access Control). Each represents a step forward in expressiveness —
            from simple per-object lists, to role assignments, to fully dynamic attribute-driven
            decisions.
          </p>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Model</TableHead>
                <TableHead>How It Works</TableHead>
                <TableHead className="w-[110px]">Granularity</TableHead>
                <TableHead className="w-[110px]">Flexibility</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accessControlModels.map((row) => (
                <TableRow key={row.model}>
                  <TableCell className="font-medium">{row.model}</TableCell>
                  <TableCell className="text-muted-foreground">{row.description}</TableCell>
                  <TableCell>
                    <Badge variant={granularityVariant[row.granularity]}>
                      {row.granularity}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={granularityVariant[row.flexibility]}>
                      {row.flexibility}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DocSection>

        <Separator />

        <DocSection title="How ABAC Works">
          <p className="text-sm text-muted-foreground leading-relaxed">
            ABAC evaluates attributes drawn from four categories to make every access decision.{' '}
            <strong>Subject</strong> attributes describe the requester (department, clearance
            level, role). <strong>Resource</strong> attributes describe the data being accessed
            (table name, sensitivity classification, owner). <strong>Action</strong> attributes
            capture what the subject wants to do (SELECT, UPDATE, DELETE). And{' '}
            <strong>Environment</strong> attributes add situational context (time of day, IP
            address, device type). A policy engine evaluates all four categories together and
            returns a Permit, Deny, or obligation — such as "mask column X."
          </p>

          <MermaidDiagram
            src="/docs/diagrams/abac-decision-flow.svg"
            alt="ABAC decision flow showing attributes feeding into policy engine"
            caption="ABAC evaluates attributes from four categories to reach an access decision"
          />
        </DocSection>

        <Separator />

        <DocSection title="Why Choose ABAC?">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {abacBenefits.map((benefit) => {
              const Icon = benefit.icon
              return (
                <Card key={benefit.title}>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <CardTitle className="text-sm">{benefit.title}</CardTitle>
                    </div>
                    <CardDescription className="text-xs leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              )
            })}
          </div>
        </DocSection>

        <Separator />

        <DocSection title="Real-World Example">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Finance analyst queries the Employees table</AlertTitle>
            <AlertDescription className="mt-2 space-y-2">
              <p>
                A finance analyst runs a SELECT on the <code className="rounded bg-muted px-1 py-0.5 text-xs">Employees</code> table.
                The PDP evaluates four attribute categories in real time:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>
                  <strong>Subject:</strong> <code className="rounded bg-muted px-1 py-0.5 text-xs">department = &quot;finance&quot;</code>,{' '}
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">clearance = &quot;standard&quot;</code>
                </li>
                <li>
                  <strong>Resource:</strong> <code className="rounded bg-muted px-1 py-0.5 text-xs">table = &quot;Employees&quot;</code>,{' '}
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">column.ssn.sensitivity = &quot;high&quot;</code>
                </li>
                <li>
                  <strong>Action:</strong> <code className="rounded bg-muted px-1 py-0.5 text-xs">action = &quot;SELECT&quot;</code>
                </li>
                <li>
                  <strong>Environment:</strong> <code className="rounded bg-muted px-1 py-0.5 text-xs">time = business_hours</code>,{' '}
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">network = &quot;corporate&quot;</code>
                </li>
              </ul>
              <p>
                <strong>Result:</strong> Permit with an obligation to mask the SSN column.
                The analyst sees every other column, but SSNs appear as <code className="rounded bg-muted px-1 py-0.5 text-xs">***-**-****</code>.
                No role change, no ticket — the policy handles it automatically.
              </p>
            </AlertDescription>
          </Alert>
        </DocSection>

        <DocNav
          prev={{ title: 'Getting Started', url: '/docs/getting-started' }}
          next={{ title: 'XACML Basics', url: '/docs/xacml' }}
        />
      </div>
    </>
  )
}
